import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, LogOut, Save, Link2, MapPin, Globe, CheckCircle2, Plus, Store, Lock, KeyRound, CreditCard, Zap, Radio, ExternalLink, Copy, XCircle, ScanLine } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { it as itLocale } from "date-fns/locale";
import { toast, Toaster } from "sonner";

import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ThemeToggle from "@/components/ThemeToggle";
import { AUTH, PROFILE, DASH, CHANGE, CARD, LANDING } from "@/constants/testIds/app";
import QrCodeSection from "@/components/QrCodeSection";
import MyCardRequestsSection from "@/components/MyCardRequestsSection";
import GbpConnectPanel from "@/components/GbpConnectPanel";
import PlanTab from "@/components/PlanTab";
import ScheduledReportsSection from "@/components/ScheduledReportsSection";

function Section({ title, subtitle, children }) {
  return (
    <motion.section
      initial={{ y: 16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-[var(--bg-2)] border border-[var(--border-c)] rounded-xl p-8 shadow-[0_2px_12px_rgba(45,41,38,0.04)]"
    >
      <div className="mb-6">
        <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
          {subtitle}
        </div>
        <h2 className="mt-1 font-serif text-2xl text-[var(--fg-1)] tracking-tight">{title}</h2>
      </div>
      {children}
    </motion.section>
  );
}

function Field({ label, testId, value, onChange, placeholder, icon: Icon, type = "text" }) {
  return (
    <div>
      <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
        {label}
      </Label>
      <div className="mt-2 relative">
        {Icon && (
          <Icon
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]"
          />
        )}
        <Input
          data-testid={testId}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          type={type}
          className={Icon ? "pl-9" : ""}
        />
      </div>
    </div>
  );
}

export default function Profile() {
  const { user, logout, refresh, loading: authLoading } = useAuth();
  const { theme } = useTheme();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [savingUser, setSavingUser] = useState(false);
  const [businesses, setBusinesses] = useState([]);
  const [businessesLoading, setBusinessesLoading] = useState(true);
  const [businessesError, setBusinessesError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [newBizName, setNewBizName] = useState("");
  const [newBizAddress, setNewBizAddress] = useState("");
  const [newBizWebsite, setNewBizWebsite] = useState("");
  const [newBizGoogleUrl, setNewBizGoogleUrl] = useState("");
  const [creating, setCreating] = useState(false);

  // Change password
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  // Card actions
  const [cardBusy, setCardBusy] = useState(null); // business_id in flight
  const [confirmAction, setConfirmAction] = useState(null); // { type: 'associate'|'deactivate', biz }
  const [copiedFor, setCopiedFor] = useState(null);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || "");
      setLastName(user.last_name || "");
    }
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    (async () => {
      setBusinessesLoading(true);
      setBusinessesError(null);
      try {
        const { data } = await api.get("/businesses");
        if (!cancelled) setBusinesses(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          // Silent — surfaced to user via businessesError banner below
          console.debug("[Profile] load businesses failed:", e?.response?.status || e?.message);
          setBusinessesError("Impossibile caricare le tue attività. Riprova più tardi.");
        }
      } finally {
        if (!cancelled) setBusinessesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  // Defensive: never render an empty/black screen if auth state is transitioning or user data is missing.
  if (authLoading || !user) {
    return (
      <div
        data-testid="profile-loading"
        className="min-h-screen bg-[var(--bg-1)] flex items-center justify-center px-6"
      >
        <div className="text-center">
          <div className="font-serif text-3xl text-[var(--fg-1)] tracking-tight">
            Recen<span className="italic text-[#00F2F4]">Up</span>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-[0.25em] text-[var(--fg-3)] font-sans animate-pulse">
            Caricamento profilo…
          </div>
        </div>
      </div>
    );
  }

  const saveUser = async () => {
    setSavingUser(true);
    try {
      await api.patch("/auth/me", { first_name: firstName, last_name: lastName });
      await refresh();
      toast.success("Profilo aggiornato");
    } catch {
      toast.error("Errore durante il salvataggio");
    } finally {
      setSavingUser(false);
    }
  };

  const updateBusinessField = (id, field, value) => {
    setBusinesses((prev) =>
      prev.map((b) => (b.business_id === id ? { ...b, [field]: value } : b))
    );
  };

  const saveBusiness = async (biz) => {
    try {
      await api.patch(`/businesses/${biz.business_id}`, {
        name: biz.name,
        address: biz.address,
        website_url: biz.website_url,
        google_business_url: biz.google_business_url,
      });
      toast.success(`"${biz.name}" salvata`);
    } catch {
      toast.error("Errore durante il salvataggio");
    }
  };

  const connectGbp = async () => {
    try {
      const { data } = await api.post("/gbp/connect");
      if (data.configured && data.auth_url) {
        window.location.href = data.auth_url;
      } else {
        toast.info(data.message || "Integrazione non ancora disponibile.", { duration: 6000 });
      }
    } catch {
      toast.error("Errore durante la connessione");
    }
  };

  const reloadBusinesses = async () => {
    try {
      const { data } = await api.get("/businesses");
      setBusinesses(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const createBusiness = async () => {
    if (!newBizName.trim()) {
      toast.error("Inserisci un nome per l'attività");
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post("/businesses", {
        name: newBizName.trim(),
        address: newBizAddress.trim() || null,
        website_url: newBizWebsite.trim() || null,
        google_business_url: newBizGoogleUrl.trim() || null,
      });
      setBusinesses((prev) => [...prev, data]);
      setNewBizName("");
      setNewBizAddress("");
      setNewBizWebsite("");
      setNewBizGoogleUrl("");
      setAddOpen(false);
      toast.success("Attività creata");
    } catch {
      toast.error("Errore durante la creazione");
    } finally {
      setCreating(false);
    }
  };

  const changePassword = async () => {
    if (newPassword.length < 6) {
      toast.error("La nuova password deve avere almeno 6 caratteri");
      return;
    }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword || null,
        new_password: newPassword,
      });
      setCurrentPassword("");
      setNewPassword("");
      toast.success("Password aggiornata");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'aggiornamento");
    } finally {
      setPwSaving(false);
    }
  };

  const runCardAction = async () => {
    if (!confirmAction) return;
    const { type, biz } = confirmAction;
    setCardBusy(biz.business_id);
    try {
      const url =
        type === "associate"
          ? `/businesses/${biz.business_id}/card/associate`
          : `/businesses/${biz.business_id}/card/deactivate`;
      const { data } = await api.post(url);
      setBusinesses((prev) => prev.map((b) => (b.business_id === biz.business_id ? data : b)));
      toast.success(type === "associate" ? "Nuova card associata" : "Card disattivata");
      setConfirmAction(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Errore durante l'operazione");
    } finally {
      setCardBusy(null);
    }
  };

  const landingUrlFor = (biz) => {
    if (!biz.card_id) return "";
    return `${window.location.origin}/r/${biz.card_id}`;
  };

  const copyLandingUrl = async (biz) => {
    const url = landingUrlFor(biz);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedFor(biz.business_id);
      toast.success("Link copiato");
      setTimeout(() => setCopiedFor((c) => (c === biz.business_id ? null : c)), 1800);
    } catch {
      toast.error("Impossibile copiare");
    }
  };

  const formatLastScan = (iso) => {
    if (!iso) return "Nessuna scansione ancora";
    try {
      return formatDistanceToNow(new Date(iso), { locale: itLocale, addSuffix: true });
    } catch {
      return "—";
    }
  };

  const formatActivated = (iso) => {
    if (!iso) return "—";
    try {
      return format(new Date(iso), "d MMM yyyy", { locale: itLocale });
    } catch {
      return "—";
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-1)]" data-testid={PROFILE.container}>
      <Toaster position="top-right" theme={theme} />

      <header className="sticky top-0 z-40 bg-[var(--glass-bg)] backdrop-blur-xl border-b border-[var(--border-c)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-6">
            <div className="font-serif text-3xl text-[var(--fg-1)] tracking-tight leading-none">
              Recen<span className="italic text-[#00F2F4]">Up</span>
            </div>
            <Link
              to="/dashboard"
              data-testid={PROFILE.backLink}
              className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[var(--fg-3)] hover:text-[var(--fg-1)]"
              style={{ transition: "color 0.2s ease" }}
            >
              <ArrowLeft size={14} /> Dashboard
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle />
            <div className="text-xs text-[var(--fg-3)] font-sans hidden md:block">
              {user?.email}
            </div>
            <button
              data-testid={AUTH.logoutBtn}
              onClick={logout}
              className="h-9 w-9 rounded-full hover:bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-2)]"
              style={{ transition: "background-color 0.2s ease" }}
              title="Esci"
            >
              <LogOut size={16} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10 space-y-8">
        <div>
          <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[var(--fg-3)]">
            Impostazioni
          </div>
          <h1 className="mt-2 font-serif text-4xl md:text-5xl text-[var(--fg-1)] tracking-tight">
            Il tuo profilo
          </h1>
          <p className="mt-2 text-sm text-[var(--fg-3)] max-w-xl">
            Aggiorna le tue informazioni personali e i dati delle tue attività, e collega Google Business Profile per sincronizzare le recensioni.
          </p>
        </div>

        {/* User profile */}
        <Section title="Dati personali" subtitle="Utente">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field
              label="Nome"
              testId={PROFILE.firstNameInput}
              value={firstName}
              onChange={setFirstName}
              placeholder="Marco"
            />
            <Field
              label="Cognome"
              testId={PROFILE.lastNameInput}
              value={lastName}
              onChange={setLastName}
              placeholder="Rossi"
            />
          </div>
          <div className="mt-6 flex justify-end">
            <Button
              data-testid={PROFILE.saveProfileBtn}
              onClick={saveUser}
              disabled={savingUser}
              className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2"
            >
              <Save size={14} /> Salva profilo
            </Button>
          </div>
        </Section>

        {/* Change password */}
        <Section title="Sicurezza" subtitle="Password">
          <div data-testid={CHANGE.section}>
            <p className="text-sm text-[var(--fg-3)] mb-5 leading-relaxed">
              Cambia la password del tuo account. Se hai effettuato l'accesso con Google e non hai mai impostato una password, lascia vuoto il campo "Password attuale".
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Password attuale
                </Label>
                <div className="mt-2 relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                  <Input
                    data-testid={CHANGE.currentInput}
                    type="password"
                    autoComplete="current-password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Lascia vuoto se non l'hai mai impostata"
                    className="pl-9"
                  />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                  Nuova password
                </Label>
                <div className="mt-2 relative">
                  <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--fg-4)]" />
                  <Input
                    data-testid={CHANGE.newInput}
                    type="password"
                    autoComplete="new-password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Almeno 6 caratteri"
                    className="pl-9"
                  />
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                data-testid={CHANGE.submitBtn}
                onClick={changePassword}
                disabled={pwSaving || !newPassword}
                className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2"
              >
                <KeyRound size={14} /> Aggiorna password
              </Button>
            </div>
          </div>
        </Section>

        {/* Google Business Profile */}
        <GbpConnectPanel businesses={businesses} onBusinessesRefresh={reloadBusinesses} />

        {/* Fase 4 — Plan tab (piano attuale + gestione abbonamento) */}
        <PlanTab />

        {/* Sprint 10.A — Scheduled Reports Email (Pro + Trial) */}
        <ScheduledReportsSection businesses={businesses} />

        {/* Businesses */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-sans uppercase tracking-[0.25em] text-[var(--fg-3)]">
              Attività in gestione
            </div>
            <p className="mt-1 text-sm text-[var(--fg-3)]">
              {businessesLoading
                ? "Caricamento in corso…"
                : `${businesses.length} ${businesses.length === 1 ? "attività" : "attività"} · aggiungi tutte quelle che vuoi`}
            </p>
          </div>
          <button
            data-testid={DASH.addBusinessBtn}
            onClick={() => setAddOpen(true)}
            className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans"
            style={{ transition: "background-color 0.2s ease" }}
          >
            <Plus size={16} strokeWidth={2} /> Aggiungi attività
          </button>
        </div>

        {businessesError && (
          <div
            data-testid="profile-businesses-error"
            className="rounded-xl border border-[#C6423B]/30 bg-[#C6423B]/8 p-5 text-sm text-[var(--fg-2)]"
          >
            <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[#C6423B] font-semibold mb-1">
              Errore
            </div>
            {businessesError}
          </div>
        )}

        {businessesLoading && !businessesError && (
          <div
            data-testid="profile-businesses-loading"
            className="rounded-xl border border-dashed border-[var(--border-c)] p-8 text-center text-sm text-[var(--fg-3)]"
          >
            Sto caricando le tue attività…
          </div>
        )}

        {!businessesLoading && !businessesError && businesses.length === 0 && (
          <div
            data-testid="profile-businesses-empty"
            className="rounded-xl border border-dashed border-[var(--border-c)] p-8 text-center"
          >
            <div className="font-serif text-xl text-[var(--fg-1)] tracking-tight">
              Nessuna attività ancora
            </div>
            <p className="mt-2 text-sm text-[var(--fg-3)]">
              Aggiungi la tua prima attività per iniziare a monitorare le recensioni Google.
            </p>
          </div>
        )}

        {businesses.map((biz) => (
          <Section
            key={biz.business_id}
            title={biz.name}
            subtitle="Attività"
          >
            <div
              data-testid={PROFILE.businessCard(biz.business_id)}
              className="grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <Field
                label="Nome attività"
                testId={PROFILE.bizNameInput(biz.business_id)}
                value={biz.name}
                onChange={(v) => updateBusinessField(biz.business_id, "name", v)}
              />
              <Field
                label="Via / Indirizzo"
                icon={MapPin}
                testId={PROFILE.bizAddressInput(biz.business_id)}
                value={biz.address}
                onChange={(v) => updateBusinessField(biz.business_id, "address", v)}
                placeholder="Via Roma 42, Milano"
              />
              <Field
                label="Sito web"
                icon={Globe}
                testId={PROFILE.bizWebsiteInput(biz.business_id)}
                value={biz.website_url}
                onChange={(v) => updateBusinessField(biz.business_id, "website_url", v)}
                placeholder="https://www.trattoriadaluigi.it"
              />
              <Field
                label="URL Google Business"
                icon={Link2}
                testId={PROFILE.bizGoogleUrlInput(biz.business_id)}
                value={biz.google_business_url}
                onChange={(v) => updateBusinessField(biz.business_id, "google_business_url", v)}
                placeholder="https://g.page/..."
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
              <button
                data-testid={PROFILE.bizConnectGbpBtn(biz.business_id)}
                onClick={() => connectGbp()}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] bg-[var(--bg-2)] px-5 py-2.5 text-sm text-[var(--fg-1)] font-sans hover:border-[var(--fg-2)]"
                style={{ transition: "border-color 0.2s ease" }}
              >
                {biz.gbp_connected ? (
                  <>
                    <CheckCircle2 size={14} className="text-[#00F2F4]" />
                    Google Business collegato
                  </>
                ) : (
                  <>
                    <Link2 size={14} />
                    Collega Google Business Profile
                  </>
                )}
              </button>
              <Button
                data-testid={PROFILE.bizSaveBtn(biz.business_id)}
                onClick={() => saveBusiness(biz)}
                className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2"
              >
                <Save size={14} /> Salva attività
              </Button>
            </div>

            {/* ---------- RecenUp Card ---------- */}
            <div
              data-testid={CARD.section(biz.business_id)}
              className="mt-8 pt-8 border-t border-[var(--border-c)]"
            >
              <div className="flex items-start justify-between gap-4 mb-6">
                <div className="flex items-start gap-3">
                  <div className="h-10 w-10 rounded-full bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-1)]">
                    <CreditCard size={17} strokeWidth={1.7} />
                  </div>
                  <div>
                    <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                      RecenUp Card
                    </div>
                    <div className="mt-0.5 font-serif text-xl text-[var(--fg-1)] tracking-tight leading-tight">
                      Card NFC dell'attività
                    </div>
                  </div>
                </div>
                <span
                  data-testid={CARD.statusBadge(biz.business_id)}
                  className={`inline-flex items-center gap-1.5 text-[10px] font-sans font-semibold uppercase tracking-[0.15em] px-3 py-1 rounded-full ${
                    biz.card_status === "active"
                      ? "bg-[#4A8A6E]/12 text-[#3E7A5E]"
                      : "bg-[#C6423B]/12 text-[#C6423B]"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      biz.card_status === "active" ? "bg-[#4A8A6E] animate-pulse" : "bg-[#C6423B]"
                    }`}
                  />
                  {biz.card_status === "active" ? "Attiva" : "Disattivata"}
                </span>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)]">
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    ID card
                  </div>
                  <div
                    data-testid={CARD.cardId(biz.business_id)}
                    className="mt-1.5 font-mono text-sm font-semibold text-[var(--fg-1)]"
                  >
                    {biz.card_id || "—"}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)]">
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Attivata il
                  </div>
                  <div
                    data-testid={CARD.activatedAt(biz.business_id)}
                    className="mt-1.5 text-sm text-[var(--fg-1)]"
                  >
                    {formatActivated(biz.card_activated_at)}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)]">
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] flex items-center gap-1">
                    <Radio size={10} /> Ultima scansione
                  </div>
                  <div
                    data-testid={CARD.lastScan(biz.business_id)}
                    className="mt-1.5 text-sm text-[var(--fg-1)] first-letter:uppercase"
                  >
                    {formatLastScan(biz.last_scan_at)}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)]">
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)] flex items-center gap-1">
                    <ScanLine size={10} /> Scansioni totali
                  </div>
                  <div
                    data-testid={CARD.totalScans(biz.business_id)}
                    className="mt-1.5 font-serif text-2xl leading-none text-[var(--fg-1)]"
                  >
                    {biz.total_scans ?? 0}
                  </div>
                </div>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  data-testid={CARD.associateBtn(biz.business_id)}
                  onClick={() => setConfirmAction({ type: "associate", biz })}
                  disabled={cardBusy === biz.business_id}
                  className="inline-flex items-center gap-2 rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] px-5 py-2.5 text-sm font-sans disabled:opacity-60"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <Zap size={14} /> Associa nuova card
                </button>
                <button
                  data-testid={CARD.deactivateBtn(biz.business_id)}
                  onClick={() => setConfirmAction({ type: "deactivate", biz })}
                  disabled={cardBusy === biz.business_id || biz.card_status !== "active"}
                  className="inline-flex items-center gap-2 rounded-full border border-[#C6423B]/30 text-[#C6423B] hover:bg-[#C6423B]/5 px-5 py-2.5 text-sm font-sans disabled:opacity-40"
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <XCircle size={14} /> Disattiva card
                </button>
              </div>
            </div>

            {/* ---------- Landing Page ---------- */}
            <div
              data-testid={LANDING.section(biz.business_id)}
              className="mt-8 pt-8 border-t border-[var(--border-c)]"
            >
              <div className="flex items-start gap-3 mb-6">
                <div className="h-10 w-10 rounded-full bg-[var(--bg-3)] flex items-center justify-center text-[var(--fg-1)]">
                  <Globe size={17} strokeWidth={1.7} />
                </div>
                <div>
                  <div className="text-[10px] font-sans uppercase tracking-[0.2em] text-[var(--fg-3)]">
                    Landing page
                  </div>
                  <div className="mt-0.5 font-serif text-xl text-[var(--fg-1)] tracking-tight leading-tight">
                    Link pubblico dell'attività
                  </div>
                </div>
              </div>

              <p className="text-sm text-[var(--fg-3)] leading-relaxed mb-4">
                Condividi questo link anche senza card NFC — su WhatsApp, email, social o QR-code stampato.
              </p>

              <div
                data-testid={LANDING.urlDisplay(biz.business_id)}
                className="p-4 rounded-lg bg-[var(--bg-3)] border border-[var(--border-c)] font-mono text-xs md:text-sm text-[var(--fg-2)] break-all"
              >
                {landingUrlFor(biz) || <span className="text-[var(--fg-4)]">Nessuna card associata</span>}
              </div>

              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={landingUrlFor(biz) || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={LANDING.openBtn(biz.business_id)}
                  className={`inline-flex items-center gap-2 rounded-full bg-[#00F2F4] hover:bg-[#00D6D8] text-[#0A0A0A] px-5 py-2.5 text-sm font-sans font-semibold ${
                    !biz.card_id || biz.card_status !== "active" ? "opacity-50 pointer-events-none" : ""
                  }`}
                  style={{ transition: "background-color 0.2s ease" }}
                >
                  <ExternalLink size={14} /> Apri Landing Page
                </a>
                <button
                  data-testid={LANDING.copyBtn(biz.business_id)}
                  onClick={() => copyLandingUrl(biz)}
                  disabled={!biz.card_id}
                  className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans disabled:opacity-40"
                  style={{ transition: "border-color 0.2s ease" }}
                >
                  {copiedFor === biz.business_id ? (
                    <>
                      <CheckCircle2 size={14} className="text-[#4A8A6E]" /> Copiato
                    </>
                  ) : (
                    <>
                      <Copy size={14} /> Copia link
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* ---------- QR Code ---------- */}
            <QrCodeSection business={biz} />
          </Section>
        ))}

        {/* My RecenUp Card requests */}
        <MyCardRequestsSection user={user} businesses={businesses} />

        {/* Add business tile */}
        <motion.button
          initial={{ y: 16, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          onClick={() => setAddOpen(true)}
          data-testid="add-business-tile"
          className="w-full group border-2 border-dashed border-[var(--border-c)] hover:border-[var(--fg-2)] rounded-xl p-8 flex flex-col items-center justify-center gap-3 bg-transparent hover:bg-[var(--bg-2)] text-[var(--fg-3)] hover:text-[var(--fg-1)]"
          style={{ transition: "border-color 0.2s ease, background-color 0.2s ease, color 0.2s ease" }}
        >
          <div className="h-12 w-12 rounded-full bg-[var(--bg-3)] group-hover:bg-[var(--border-c)] flex items-center justify-center">
            <Plus size={22} strokeWidth={1.8} />
          </div>
          <div className="font-serif text-xl">Aggiungi un'altra attività</div>
          <div className="text-xs font-sans">Gestisci più attività dallo stesso profilo</div>
        </motion.button>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent className="bg-[var(--bg-1)] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Nuova attività</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field
                label="Nome attività"
                testId={DASH.newBusinessNameInput}
                value={newBizName}
                onChange={setNewBizName}
                placeholder="Trattoria da Luigi"
              />
              <Field
                label="Via / Indirizzo"
                icon={MapPin}
                testId={DASH.newBusinessAddressInput}
                value={newBizAddress}
                onChange={setNewBizAddress}
                placeholder="Via Roma 42, Milano"
              />
              <Field
                label="Sito web"
                icon={Globe}
                testId="new-business-website-input"
                value={newBizWebsite}
                onChange={setNewBizWebsite}
                placeholder="https://..."
              />
              <Field
                label="URL Google Business"
                icon={Link2}
                testId="new-business-gbp-input"
                value={newBizGoogleUrl}
                onChange={setNewBizGoogleUrl}
                placeholder="https://g.page/..."
              />
            </div>
            <DialogFooter>
              <Button
                data-testid={DASH.saveBusinessBtn}
                onClick={createBusiness}
                disabled={creating}
                className="rounded-full bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)] gap-2"
              >
                <Plus size={14} /> Crea attività
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Confirm card action */}
        <Dialog open={!!confirmAction} onOpenChange={(o) => !o && setConfirmAction(null)}>
          <DialogContent className="bg-[var(--bg-1)] max-w-md">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl text-[var(--fg-1)]">
                {confirmAction?.type === "associate" ? "Associa nuova card" : "Disattiva card"}
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-[var(--fg-2)] leading-relaxed">
              {confirmAction?.type === "associate" ? (
                <>
                  Verrà generato un nuovo ID card per <span className="font-semibold text-[var(--fg-1)]">{confirmAction?.biz?.name}</span>. La card attuale <span className="font-mono text-xs">{confirmAction?.biz?.card_id}</span> smetterà di funzionare.
                  <br /><br />
                  <span className="text-[var(--fg-3)]">Tutte le statistiche, le recensioni e i dati dell'attività resteranno invariati.</span>
                </>
              ) : (
                <>
                  Segnalando la card come persa o rubata la disattiverai immediatamente. La card <span className="font-mono text-xs">{confirmAction?.biz?.card_id}</span> non funzionerà più fino a quando non ne associ una nuova.
                </>
              )}
            </p>
            <DialogFooter className="gap-2">
              <button
                onClick={() => setConfirmAction(null)}
                className="inline-flex items-center gap-2 rounded-full border border-[var(--border-c)] hover:border-[var(--fg-2)] text-[var(--fg-1)] px-5 py-2.5 text-sm font-sans"
                style={{ transition: "border-color 0.2s ease" }}
              >
                Annulla
              </button>
              <button
                data-testid={
                  confirmAction?.type === "associate"
                    ? CARD.confirmAssociateBtn(confirmAction?.biz?.business_id)
                    : CARD.confirmDeactivateBtn(confirmAction?.biz?.business_id)
                }
                onClick={runCardAction}
                disabled={cardBusy === confirmAction?.biz?.business_id}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-sans font-semibold disabled:opacity-60 ${
                  confirmAction?.type === "associate"
                    ? "bg-[var(--fg-1)] hover:bg-[var(--fg-2)] text-[var(--bg-1)]"
                    : "bg-[#C6423B] hover:bg-[#B0392F] text-white"
                }`}
                style={{ transition: "background-color 0.2s ease" }}
              >
                {confirmAction?.type === "associate" ? (
                  <>
                    <Zap size={14} /> Conferma associazione
                  </>
                ) : (
                  <>
                    <XCircle size={14} /> Sì, disattiva
                  </>
                )}
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
